// Third-party upload handle: trades the single-use emailed `#h=` handle for a purpose-scoped tusd upload credential.
// The credential is NOT a session token: it lives only in sessionStorage and is sent only as the tusd Bearer. Never logged.

import { uploadRedeemUrl } from 'services/auth/constants';

const UPLOAD_CREDENTIAL_KEY = 'document_upload_token';
const REQUEST_TIMEOUT_MS = 15_000;

export type UploadRequestFile = {
  archive_id: string,
  description: string
};

export type UploadRedeemFields = {
  token: string,
  name: string,
  number: string,
  files: UploadRequestFile[],
  order_id: string,
  party: string,
  exp: number
};

export type UploadRedeemErrorCode = 'invalid' | 'used' | 'rate_limited' | 'network';

export type UploadRedeemResult =
  | ({ ok: true } & UploadRedeemFields)
  | { ok: false; code: UploadRedeemErrorCode };

const classifyStatus = (status: number): UploadRedeemErrorCode => {
  if (status === 404) return 'used';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'network';
  return 'invalid';
};

const isString = (value: unknown): value is string => typeof value === 'string';

const parseFiles = (value: unknown): UploadRequestFile[] | null => {
  if (!Array.isArray(value)) return null;
  const files = value.filter((file) => isString(file?.archive_id) && isString(file?.description));
  return files.length === value.length ? files.map(({ archive_id, description }) => ({ archive_id, description })) : null;
};

const parseBody = (body: any): UploadRedeemFields | null => {
  const files = parseFiles(body?.files);
  const valid = isString(body?.token) && body.token !== ''
    && isString(body.order_id) && isString(body.party)
    && typeof body.exp === 'number' && files !== null;
  if (!valid) return null;
  return {
    token: body.token,
    name: isString(body.name) ? body.name : '',
    number: isString(body.number) ? body.number : '',
    files: files!,
    order_id: body.order_id,
    party: body.party,
    exp: body.exp,
  };
};

const readBody = async (response: Response): Promise<UploadRedeemFields | null> => {
  try {
    return parseBody(await response.json());
  } catch {
    return null;
  }
};

export const redeemUploadHandle = async (handle: string): Promise<UploadRedeemResult> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(uploadRedeemUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ h: handle }),
      signal: controller.signal,
    });
    if (!response.ok) return { ok: false, code: classifyStatus(response.status) };
    const fields = await readBody(response);
    // A 2xx without a usable body cannot be retried: the handle is single-use.
    return fields ? { ok: true, ...fields } : { ok: false, code: 'invalid' };
  } catch {
    return { ok: false, code: 'network' };
  } finally {
    clearTimeout(timer);
  }
};

export const setUploadCredential = (token: string): void => {
  sessionStorage.setItem(UPLOAD_CREDENTIAL_KEY, token);
};

export const getUploadCredential = (): string | null => sessionStorage.getItem(UPLOAD_CREDENTIAL_KEY);

export const clearUploadCredential = (): void => {
  sessionStorage.removeItem(UPLOAD_CREDENTIAL_KEY);
};
